import "es6-shim";

import React from "react";
import PropTypes from "prop-types";
import { ContentState, convertFromHTML, Editor, EditorState, getDefaultKeyBinding, RichUtils } from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import Icon from "../../../components/Icon";
import Modal from "../../../components/Modal";

const styleMap =
	{
		CODE: {
			backgroundColor: "rgba(0, 0, 0, 0.05)",
			fontFamily: "'Inconsolata', 'Menlo', 'Consolas', monospace",
			fontSize: 16,
			padding: 2
		}
	};

function getBlockStyle( block )
{
	switch ( block.getType() )
	{
		case "blockquote":
			return "blockquote";
		default:
			return null;
	}
} // function getBlockStyle(...)

class StyleButton extends React.Component
{
	constructor( props )
	{
		super( props );
		
		this.onToggle = ( e ) =>
		{
			e.preventDefault();
			this.props.onToggle( this.props.style );
		};
		
	} // function constructor(...)
	
	render()
	{
		let className = "btn btn-small btn-flat normal-text margin-right";
		if ( this.props.active )
		{
			className += " white secondary-color-text";
		}
		else
		{
			className += " white-text hover-light-grey hover-secondary-color-text";
		}
		
		return (
			<span className={className} onMouseDown={this.onToggle}>
		        {this.props.label}
		      </span>
		);
		
	} // function render()
	
} // class

const BLOCK_TYPES = [
	{ label: "H1", style: "header-one" },
	{ label: "H2", style: "header-two" },
	{ label: "H3", style: "header-three" },
	{ label: "H4", style: "header-four" },
	{ label: "H5", style: "header-five" },
	{ label: "H6", style: "header-six" },
	{ label: <Icon iconName={"quote"}/>, style: "blockquote" },
	{ label: <Icon iconName={"unorderedList"}/>, style: "unordered-list-item" },
	{ label: <Icon iconName={"orderedList"}/>, style: "ordered-list-item" },
	{ label: "Code Block", style: "code-block" }
];

const BlockStyleControls = ( props ) =>
{
	const { editorState, controlTypes = null } = props;
	const controls = controlTypes === null ? BLOCK_TYPES : controlTypes;
	
	const selection = editorState.getSelection();
	const blockType = editorState
		.getCurrentContent()
		.getBlockForKey( selection.getStartKey() )
		.getType();
	
	return (
		<span className="editor-controls">
			{
				controls.map( ( type ) =>
					<StyleButton
						key={`${type.style}_${props.additionalKey}`}
						active={type.style === blockType}
						label={type.label}
						onToggle={props.onToggle}
						style={type.style}
					/>
				)
			}
		</span>
	);
};

const INLINE_STYLES = [
	{ label: <Icon iconName={"bold"}/>, style: "BOLD" },
	{ label: <Icon iconName={"italic"}/>, style: "ITALIC" },
	{ label: <Icon iconName={"underlined"}/>, style: "UNDERLINE" }
	// {label: 'Monospace', style: 'CODE'},
];

const InlineStyleControls = ( props ) =>
{
	const { controlTypes = null } = props;
	const controls = controlTypes === null ? INLINE_STYLES : controlTypes;
	
	const currentStyle = props.editorState.getCurrentInlineStyle();
	
	return (
		<span className="editor-controls">
			{
				controls.map( type =>
					<StyleButton
						key={`${type.style}_${props.additionalKey}`}
						active={currentStyle.has( type.style )}
						label={type.label}
						onToggle={props.onToggle}
						style={type.style}
					/>
				)
			}
		</span>
	);
	
};

export default class ModalEditor extends React.Component
{
	constructor( props )
	{
		super( props );
		
		if ( typeof props.content !== "undefined" && props.content !== null )
		{
			const blocksFromHTML = convertFromHTML( props.content );
			
			if ( blocksFromHTML.contentBlocks !== null )
			{
				const state = ContentState.createFromBlockArray(
					blocksFromHTML.contentBlocks,
					blocksFromHTML.entityMap
				);
				
				this.state = {
					editorState: EditorState.createWithContent( state )
				};
			}
			else
			{
				this.state = { editorState: EditorState.createEmpty() };
			}
		}
		else
		{
			this.state = { editorState: EditorState.createEmpty() };
		}
		
		this.onFocus = this.onFocus.bind( this );
		this.onChange = ( editorState ) => this.setState( { editorState } );
		this.handleKeyCommand = this.handleKeyCommand.bind( this );
		this.mapKeyToEditorCommand = this.mapKeyToEditorCommand.bind( this );
		this.toggleBlockType = this.toggleBlockType.bind( this );
		this.toggleInlineStyle = this.toggleInlineStyle.bind( this );
		this.handleResetEditor = this.handleResetEditor.bind( this );
		
	} // function constructor(...)
	
	onFocus()
	{
		this.editor.focus();
		
	} // function onFocus(...)
	
	handleKeyCommand( command, editorState )
	{
		const newState = RichUtils.handleKeyCommand( editorState, command );
		
		if ( newState )
		{
			this.onChange( newState );
			return "handled";
		}
		
		return "not-handled";
		
	} // function handleKeyCommand(...)
	
	mapKeyToEditorCommand( e )
	{
		switch ( e.keyCode )
		{
			case 9: // TAB
				const newEditorState = RichUtils.onTab(
					e,
					this.state.editorState,
					4 /* maxDepth */
				);
				
				if ( newEditorState !== this.state.editorState )
				{
					this.onChange( newEditorState );
				}
				
				return;
			
			default:
				return getDefaultKeyBinding( e );
			
		}
		
	} // function mapKeyToEditorCommand(...)
	
	toggleBlockType( blockType )
	{
		this.onChange(
			RichUtils.toggleBlockType(
				this.state.editorState,
				blockType
			)
		);
		
	} // function toggleBlockType(...)
	
	toggleInlineStyle( inlineStyle )
	{
		this.onChange(
			RichUtils.toggleInlineStyle(
				this.state.editorState,
				inlineStyle
			)
		);
		
	} // function toggleInlineStyle(...)
	
	handleResetEditor()
	{
		if ( this.props.content )
		{
			const blocksFromHTML = convertFromHTML( this.props.content );
			
			this.setState( {
				editorState: blocksFromHTML.contentBlocks
					? EditorState.createWithContent( ContentState.createFromBlockArray(
						blocksFromHTML.contentBlocks,
						blocksFromHTML.entityMap
					) )
					: EditorState.createEmpty()
			} );
		}
		else
		{
			this.setState( { editorState: EditorState.createEmpty() } );
		}
		
	} // function handleResetEditor(...)
	
	render()
	{
		const { editorState } = this.state;
		
		let classNameInner = "editor white";
		const contentState = editorState.getCurrentContent();
		
		if ( !contentState.hasText() )
		{
			if ( contentState.getBlockMap().first().getType() !== "unstyled" )
			{
				classNameInner += " hidePlaceholder";
			}
		}
		
		return (
			<Modal {...this.props}
			       footer={
				       <React.Fragment>
					       <button onClick={() => this.props.onSubmit( contentState.hasText() ? stateToHTML( contentState ) : "" )}
					               className={"margin-right"}
					               data-modal={"confirm"}>
						       <Icon size={18} verticalAlign={"sub"} iconName={"save"}/><span>Speichern</span>
					       </button>
					       <button onClick={() => this.handleResetEditor()}
					               className={"margin-right secondary-color"}
					               data-modal={"confirm"}>
						       <Icon iconName={"discard"}/><span>Verwerfen</span>
					       </button>
				       </React.Fragment>
			       }
			>
				
				<div className="Editor-root collection with-header">
					<div className={"collection-header secondary-color"}>
						<InlineStyleControls
							editorState={editorState}
							onToggle={this.toggleInlineStyle}
							controlTypes={this.props.inlineControlTypes}
							additionalKey={this.props.additionalKey}
						/>
						<BlockStyleControls
							editorState={editorState}
							onToggle={this.toggleBlockType}
							controlTypes={this.props.blockControlTypes}
							additionalKey={this.props.additionalKey}
						/>
					</div>
					<div className={"collection-item"} onClick={this.onFocus}>
						<div className={classNameInner}>
							<Editor
								blockStyleFn={getBlockStyle}
								customStyleMap={styleMap}
								editorState={editorState}
								handleKeyCommand={this.handleKeyCommand}
								// keyBindingFn={this.mapKeyToEditorCommand}
								keyBindingFn={this.mapKeyToEditorCommand}
								onChange={this.onChange}
								ref={( ref ) => this.editor = ref}
								spellCheck={true}
							/>
						</div>
					</div>
				</div>
			
			</Modal>
		);
		
	} // function render()
	
} // class

ModalEditor.propTypes = {
	onSubmit: PropTypes.func.isRequired,
	trigger: PropTypes.object.isRequired,
	additionalKey: PropTypes.string.isRequired,
	inlineControlTypes: PropTypes.array,
	blockControlTypes: PropTypes.array,
	className: PropTypes.string,
	content: PropTypes.string,
	headerTitle: PropTypes.string
};
